import React from 'react';
import { Box, Typography, Stack, Chip, useTheme, IconButton, Tooltip, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { formatFirestoreTimestamp } from '../util/firebase';

const getSuccessRateColor = (rate) => {
  if (rate >= 70) return '#10B981'; // Emerald green
  if (rate >= 40) return '#F59E0B'; // Amber
  return '#EF4444'; // Red
};

export default function BankInsightsTile({ bank, userBonus, totalUsers, successRate, onVote, onReset, isMobile }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Calculate progress percentage from userProgress
  const progressPercentage = userBonus?.status === 'completed'
    ? 100
    : userBonus?.status === null || !userBonus
    ? 0
    : (userBonus?.userProgress?.progressPercentage || userBonus?.progressPercentage || 0);

  const isCompleted = userBonus?.status === 'completed' || Boolean(userBonus?.completedAt);
  const completedDate = userBonus?.completedAt || userBonus?.userProgress?.completedAt;

  const hasCompletions = bank.totalAttempts > 0 || bank.successfulCompletions > 0 || bank.failedCompletions > 0;

  return (
    <Box
      sx={{
        p: 0,
        background: isDark
          ? `linear-gradient(135deg, ${alpha('#1F2937', 0.6)} 0%, ${alpha('#111827', 0.8)} 100%)`
          : `linear-gradient(135deg, ${alpha('#FFFFFF', 0.95)} 0%, ${alpha('#F9FAFB', 0.98)} 100%)`,
        border: `1px solid ${isDark ? alpha('#374151', 0.5) : alpha('#E5E7EB', 0.8)}`,
        borderRadius: isMobile ? 0 : 4,
        width: '100%',
        boxShadow: isMobile ? 'none' : `0 20px 60px ${alpha(theme.palette.primary.main, isDark ? 0.15 : 0.08)}`,
        backdropFilter: 'blur(20px)',
        overflow: 'hidden',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          boxShadow: isMobile ? 'none' : `0 24px 70px ${alpha(theme.palette.primary.main, isDark ? 0.2 : 0.12)}`,
          transform: isMobile ? 'none' : 'translateY(-2px)',
        }
      }}
    >
      <Stack spacing={0}>
        {/* Stats Section */}
        {(hasCompletions || userBonus) && (
          <Box sx={{ p: 3 }}>
            <Stack direction="row" spacing={2}>
              {/* Community Success Rate */}
              {hasCompletions && (
                <Box
                  sx={{
                    flex: 1,
                    p: 2.5,
                    background: `linear-gradient(145deg, ${alpha(getSuccessRateColor(successRate), isDark ? 0.15 : 0.08)} 0%, ${alpha(getSuccessRateColor(successRate), isDark ? 0.08 : 0.04)} 100%)`,
                    border: `2px solid ${alpha(getSuccessRateColor(successRate), isDark ? 0.3 : 0.2)}`,
                    borderRadius: 3,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 32px ${alpha(getSuccessRateColor(successRate), 0.25)}`,
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: `linear-gradient(90deg, ${getSuccessRateColor(successRate)}, ${alpha(getSuccessRateColor(successRate), 0.7)})`,
                    }
                  }}
                >
                  <Stack spacing={1.5} alignItems="center">
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Box sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: `linear-gradient(145deg, ${alpha(getSuccessRateColor(successRate), 0.2)}, ${alpha(getSuccessRateColor(successRate), 0.1)})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <TrendingUpIcon sx={{
                          fontSize: '1rem',
                          color: getSuccessRateColor(successRate),
                        }} />
                      </Box>
                      <Typography
                        variant="overline"
                        sx={{
                          color: alpha(theme.palette.text.primary, 0.85),
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          letterSpacing: '0.8px',
                          lineHeight: 1,
                        }}
                      >
                        Community Success
                      </Typography>
                    </Stack>

                    <Typography
                      sx={{
                        fontSize: '2rem',
                        fontWeight: 800,
                        lineHeight: 1,
                        background: `linear-gradient(145deg, ${getSuccessRateColor(successRate)} 0%, ${alpha(getSuccessRateColor(successRate), 0.7)} 100%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        letterSpacing: '-0.03em',
                      }}
                    >
                      {Math.round(successRate)}%
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: alpha(theme.palette.text.primary, 0.6),
                        fontSize: '0.75rem',
                        fontWeight: 500,
                      }}
                    >
                      of {totalUsers || 0} {totalUsers === 1 ? 'person' : 'people'}
                    </Typography>
                  </Stack>
                </Box>
              )}

              {/* Your Progress */}
              {userBonus && (
                <Box
                  sx={{
                    flex: 1,
                    p: 2.5,
                    background: isCompleted
                      ? `linear-gradient(145deg, ${alpha(theme.palette.success.main, isDark ? 0.15 : 0.08)} 0%, ${alpha(theme.palette.success.main, isDark ? 0.08 : 0.04)} 100%)`
                      : `linear-gradient(145deg, ${alpha(theme.palette.primary.main, isDark ? 0.15 : 0.08)} 0%, ${alpha(theme.palette.primary.main, isDark ? 0.08 : 0.04)} 100%)`,
                    border: `2px solid ${isCompleted ? alpha(theme.palette.success.main, isDark ? 0.3 : 0.2) : alpha(theme.palette.primary.main, isDark ? 0.3 : 0.2)}`,
                    borderRadius: 3,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isCompleted
                        ? `0 12px 32px ${alpha(theme.palette.success.main, 0.25)}`
                        : `0 12px 32px ${alpha(theme.palette.primary.main, 0.25)}`,
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: isCompleted
                        ? `linear-gradient(90deg, ${theme.palette.success.main}, ${alpha(theme.palette.success.main, 0.7)})`
                        : `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.7)})`,
                    }
                  }}
                >
                  <Stack spacing={1.5} alignItems="center">
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Box sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: isCompleted
                          ? `linear-gradient(145deg, ${alpha(theme.palette.success.main, 0.2)}, ${alpha(theme.palette.success.main, 0.1)})`
                          : `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.2)}, ${alpha(theme.palette.primary.main, 0.1)})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <CheckCircleIcon sx={{
                          fontSize: '1rem',
                          color: isCompleted ? theme.palette.success.main : theme.palette.primary.main,
                        }} />
                      </Box>
                      <Typography
                        variant="overline"
                        sx={{
                          color: alpha(theme.palette.text.primary, 0.85),
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          letterSpacing: '0.8px',
                          lineHeight: 1,
                        }}
                      >
                        Your Progress
                      </Typography>
                    </Stack>

                    <Typography
                      sx={{
                        fontSize: '2rem',
                        fontWeight: 800,
                        lineHeight: 1,
                        background: isCompleted
                          ? `linear-gradient(145deg, ${theme.palette.success.main} 0%, ${alpha(theme.palette.success.main, 0.7)} 100%)`
                          : `linear-gradient(145deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.7)} 100%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        letterSpacing: '-0.03em',
                      }}
                    >
                      {progressPercentage}%
                    </Typography>

                    <Chip
                      label={isCompleted ? '✓ Completed' : 'In Progress'}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        background: isCompleted
                          ? `linear-gradient(90deg, ${theme.palette.success.main}, ${alpha(theme.palette.success.main, 0.8)})`
                          : `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.8)})`,
                        color: '#FFFFFF',
                        border: 'none',
                        boxShadow: isCompleted
                          ? `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`
                          : `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                      }}
                    />
                  </Stack>
                </Box>
              )}
            </Stack>

            {/* Progress Bar */}
            {userBonus && (
              <Box sx={{ mt: 3 }}>
                <Box
                  sx={{
                    height: '6px',
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    borderRadius: '3px',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      height: '100%',
                      width: `${progressPercentage}%`,
                      background: isCompleted
                        ? `linear-gradient(90deg, ${theme.palette.success.main}, ${alpha(theme.palette.success.main, 0.8)})`
                        : `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.8)})`,
                      borderRadius: '3px',
                      transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: isCompleted
                        ? `0 0 16px ${alpha(theme.palette.success.main, 0.5)}`
                        : `0 0 16px ${alpha(theme.palette.primary.main, 0.5)}`,
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Dates */}
            {(userBonus?.startedAt || (isCompleted && completedDate)) && (
              <Stack direction="column" spacing={2} sx={{ mt: 3 }} alignItems="flex-start">
                {userBonus?.startedAt && (
                  <Box sx={{ flex: '0 0 auto', width: 'auto' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.main, 0.06)})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <AccessTimeIcon sx={{ fontSize: '1rem', color: theme.palette.primary.main }} />
                      </Box>
                      <Stack spacing={0.25}>
                        <Typography variant="caption" sx={{
                          color: alpha(theme.palette.text.primary, 0.5),
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}>
                          Started
                        </Typography>
                        <Typography variant="body2" sx={{
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          fontSize: '0.875rem',
                        }}>
                          {formatFirestoreTimestamp(userBonus.startedAt)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                )}

                {isCompleted && completedDate && (
                  <Box sx={{ flex: '0 0 auto', width: 'auto' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        background: `linear-gradient(145deg, ${alpha(theme.palette.success.main, 0.12)}, ${alpha(theme.palette.success.main, 0.06)})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <CheckCircleIcon sx={{ fontSize: '1rem', color: theme.palette.success.main }} />
                      </Box>
                      <Stack spacing={0.25}>
                        <Typography variant="caption" sx={{
                          color: alpha(theme.palette.text.primary, 0.5),
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}>
                          Completed
                        </Typography>
                        <Typography variant="body2" sx={{
                          fontWeight: 600,
                          color: theme.palette.success.main,
                          fontSize: '0.875rem',
                        }}>
                          {formatFirestoreTimestamp(completedDate)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                )}
              </Stack>
            )}
          </Box>
        )}

        {/* Divider */}
        <Box sx={{
          height: '1px',
          background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.text.primary, 0.08)}, transparent)`,
          mx: 3,
        }} />

        {/* Voting Section */}
        <Box sx={{ p: 3 }}>
          <Stack spacing={2.5} alignItems="center">
            <Typography
              variant="overline"
              sx={{
                color: alpha(theme.palette.text.primary, 0.75),
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '1.2px',
                textAlign: 'center',
              }}
            >
              Rate This Bonus
            </Typography>

            <Stack direction="row" spacing={4} justifyContent="center" alignItems="center">
              {/* Thumbs Up */}
              <Stack spacing={1.5} alignItems="center">
                <IconButton
                  onClick={() => onVote('up')}
                  sx={{
                    width: 64,
                    height: 64,
                    background: bank?.userVote === 'up'
                      ? `linear-gradient(145deg, ${alpha(theme.palette.success.main, 0.18)}, ${alpha(theme.palette.success.main, 0.1)})`
                      : `linear-gradient(145deg, ${alpha(theme.palette.text.primary, isDark ? 0.08 : 0.05)}, ${alpha(theme.palette.text.primary, isDark ? 0.04 : 0.02)})`,
                    border: `2px solid ${bank?.userVote === 'up' ? alpha(theme.palette.success.main, 0.35) : alpha(theme.palette.text.primary, 0.1)}`,
                    color: bank?.userVote === 'up' ? theme.palette.success.main : alpha(theme.palette.text.primary, 0.4),
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: `linear-gradient(145deg, ${alpha(theme.palette.success.main, 0.22)}, ${alpha(theme.palette.success.main, 0.14)})`,
                      border: `2px solid ${alpha(theme.palette.success.main, 0.45)}`,
                      color: theme.palette.success.main,
                      transform: 'translateY(-4px) scale(1.08)',
                      boxShadow: `0 12px 28px ${alpha(theme.palette.success.main, 0.3)}`,
                    },
                    '&:active': {
                      transform: 'translateY(-2px) scale(1.02)',
                    }
                  }}
                >
                  <ThumbUpIcon sx={{ fontSize: '1.75rem' }} />
                </IconButton>
                <Typography
                  variant="h6"
                  sx={{
                    color: alpha(theme.palette.text.primary, 0.8),
                    fontWeight: 700,
                    fontSize: '1.125rem',
                    minWidth: '36px',
                    textAlign: 'center',
                  }}
                >
                  {bank?.ups || 0}
                </Typography>
              </Stack>

              {/* Divider */}
              <Box sx={{
                width: '1px',
                height: '56px',
                background: `linear-gradient(180deg, transparent, ${alpha(theme.palette.text.primary, 0.12)}, transparent)`,
              }} />

              {/* Thumbs Down */}
              <Stack spacing={1.5} alignItems="center">
                <IconButton
                  onClick={() => onVote('down')}
                  sx={{
                    width: 64,
                    height: 64,
                    background: bank?.userVote === 'down'
                      ? `linear-gradient(145deg, ${alpha(theme.palette.error.main, 0.18)}, ${alpha(theme.palette.error.main, 0.1)})`
                      : `linear-gradient(145deg, ${alpha(theme.palette.text.primary, isDark ? 0.08 : 0.05)}, ${alpha(theme.palette.text.primary, isDark ? 0.04 : 0.02)})`,
                    border: `2px solid ${bank?.userVote === 'down' ? alpha(theme.palette.error.main, 0.35) : alpha(theme.palette.text.primary, 0.1)}`,
                    color: bank?.userVote === 'down' ? theme.palette.error.main : alpha(theme.palette.text.primary, 0.4),
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: `linear-gradient(145deg, ${alpha(theme.palette.error.main, 0.22)}, ${alpha(theme.palette.error.main, 0.14)})`,
                      border: `2px solid ${alpha(theme.palette.error.main, 0.45)}`,
                      color: theme.palette.error.main,
                      transform: 'translateY(-4px) scale(1.08)',
                      boxShadow: `0 12px 28px ${alpha(theme.palette.error.main, 0.3)}`,
                    },
                    '&:active': {
                      transform: 'translateY(-2px) scale(1.02)',
                    }
                  }}
                >
                  <ThumbDownIcon sx={{ fontSize: '1.75rem' }} />
                </IconButton>
                <Typography
                  variant="h6"
                  sx={{
                    color: alpha(theme.palette.text.primary, 0.8),
                    fontWeight: 700,
                    fontSize: '1.125rem',
                    minWidth: '36px',
                    textAlign: 'center',
                  }}
                >
                  {bank?.downs || 0}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
